import { useEffect, useRef, useState } from "react";
import { FeedbackFormRenderer } from "@constructorfleet/ui";
import { useFeedbackForms } from "../hooks/use-feedback-forms";
import { submitFeedbackResponse } from "../lib/feedback-api";

const AUTO_DISMISS_MS = 3 * 60 * 1000;

export function FeedbackTrigger() {
  const { forms, dismissForm } = useFeedbackForms();
  const unobtrusiveForms = forms.filter((form) => Boolean(form.unobtrusive));
  const modalForm = forms.find((form) => !form.unobtrusive) ?? null;

  const modalFormId = modalForm?.id ?? null;

  // Store error alongside the formId it belongs to so stale errors are
  // never shown after the active form changes — no ref or effect needed.
  const [submitError, setSubmitError] = useState<{
    formId: number;
    message: string;
  } | null>(null);

  // Track the current formId for the async onSubmit callback. Written in
  // an effect (not during render) to satisfy react-hooks/refs.
  const activeFormIdRef = useRef(modalFormId);
  useEffect(() => {
    activeFormIdRef.current = modalFormId;
  }, [modalFormId]);

  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const visibleIds = new Set(unobtrusiveForms.map((form) => form.id));

    for (const [formId, timer] of timersRef.current.entries()) {
      if (!visibleIds.has(formId)) {
        clearTimeout(timer);
        timersRef.current.delete(formId);
      }
    }

    for (const form of unobtrusiveForms) {
      if (timersRef.current.has(form.id)) continue;
      const timer = setTimeout(() => {
        timersRef.current.delete(form.id);
        dismissForm(form.id);
      }, AUTO_DISMISS_MS);
      timersRef.current.set(form.id, timer);
    }
  }, [dismissForm, unobtrusiveForms]);

  useEffect(
    () => () => {
      for (const timer of timersRef.current.values()) {
        clearTimeout(timer);
      }
      timersRef.current.clear();
    },
    []
  );

  if (forms.length === 0) return null;

  const visibleError =
    submitError && submitError.formId === modalFormId ? submitError.message : undefined;

  const renderForm = (form: (typeof forms)[number]) => (
    <FeedbackFormRenderer
      key={form.id}
      open={!form.unobtrusive}
      title={form.title}
      fields={form.fields}
      unobtrusive={Boolean(form.unobtrusive)}
      unobtrusivePrompt={form.unobtrusivePrompt}
      onClose={() => {
        setSubmitError(null);
        dismissForm(form.id);
      }}
      onSubmit={async (values) => {
        setSubmitError(null);
        try {
          await submitFeedbackResponse(form.id, values);
          // Only dismiss if the form is still the active modal one.
          if (activeFormIdRef.current === form.id) dismissForm(form.id);
        } catch {
          setSubmitError({
            formId: form.id,
            message: "Sorry—your feedback could not be submitted. Please try again.",
          });
        }
      }}
      error={visibleError}
    />
  );

  return (
    <>
      {unobtrusiveForms.length > 1 ? (
        <div className="cf-feedback-unobtrusive-stack">{unobtrusiveForms.map(renderForm)}</div>
      ) : (
        unobtrusiveForms.map(renderForm)
      )}
      {modalForm ? renderForm(modalForm) : null}
    </>
  );
}
